import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, Search, Trash2, Calendar, Award, ArrowLeft, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PrizeDrawAdmin() {
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      loadEntries();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', response.data.token);
      setIsAuthenticated(true);
      await loadEntries();
    } catch (error) {
      setLoginError('Invalid password. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      filterEntries();
    }
  }, [entries, searchTerm, filterType, isAuthenticated]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/prize-draw`);
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to load entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = () => {
    let filtered = [...entries];

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(entry => entry.entry_type === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.first_name.toLowerCase().includes(search) ||
        entry.last_name.toLowerCase().includes(search) ||
        entry.entry_number.toLowerCase().includes(search) ||
        entry.email.toLowerCase().includes(search)
      );
    }

    // Sort by newest first
    filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    setFilteredEntries(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Entry Number', 'First Name', 'Last Name', 'Email', 'Phone'];
    const rows = filteredEntries.map(entry => [
      new Date(entry.timestamp).toLocaleDateString(),
      entry.entry_type,
      entry.entry_number,
      entry.first_name,
      entry.last_name,
      entry.email,
      entry.phone
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prize-draw-entries-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading entries...</p>
        </div>
      </div>
    );
  }

  // Login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gray-900 border border-green-500/30 rounded-lg p-8">
          <div className="text-center mb-8">
            <Lock className="text-green-400 mx-auto mb-4" size={48} />
            <h2 className="text-3xl font-black text-gray-100">Prize Draw Admin</h2>
            <p className="text-gray-400 mt-2">Enter password to access prize draw entries</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label className="block text-gray-300 font-semibold mb-2">Admin Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                placeholder="Enter password"
                required
              />
            </div>

            {loginError && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pt-4 pb-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Sticky Navigation */}
        <div className="sticky top-4 z-50 bg-gray-800 border border-green-500/30 rounded-lg p-4 mb-8 shadow-xl" style={{backdropFilter: 'blur(10px)', backgroundColor: 'rgba(31, 41, 55, 0.95)'}}>
          <div className="flex gap-3 flex-wrap justify-center">
            <a href="/admin" className="bg-gray-700 hover:bg-gray-600 text-green-400 px-4 py-2 rounded-lg font-bold transition-all text-sm">
              Content Editor
            </a>
            <a href="/admin/prize-draws" className="text-gray-900 px-4 py-2 rounded-lg font-bold transition-all text-sm" style={{backgroundColor: '#01F9C6'}}>
              Prize Draws
            </a>
            <a href="/admin/quote-requests" className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg font-bold transition-all text-sm">
              Quote Requests
            </a>
            <a href="/admin/career-applications" className="bg-purple-500 hover:bg-purple-400 text-gray-900 px-4 py-2 rounded-lg font-bold transition-all text-sm">
              Career Applications
            </a>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <a 
            href="/admin"
            className="inline-flex items-center text-green-400 hover:text-green-300 mb-4 transition-all"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Admin Panel
          </a>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-black text-gray-100 mb-2">Prize Draw Entries</h1>
              <p className="text-gray-400">Total Entries: <span className="text-green-400 font-bold">{entries.length}</span> | Filtered: <span className="text-yellow-400 font-bold">{filteredEntries.length}</span></p>
            </div>
            <button
              onClick={exportToCSV}
              className="bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              <Download size={20} />
              Export to CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-gray-300 font-semibold mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, entry number, or email..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
                />
              </div>
            </div>

            {/* Filter by Type */}
            <div>
              <label className="block text-gray-300 font-semibold mb-2">Entry Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              >
                <option value="all">All Types</option>
                <option value="invoice">Invoice Numbers</option>
                <option value="field_ticket">Field Tickets (BOL)</option>
                <option value="swag_only">Swag Draw Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Entries Table */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg overflow-hidden">
          {filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <Award className="text-gray-600 mx-auto mb-4" size={64} />
              <p className="text-gray-400 text-xl">No entries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Entry #</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, index) => (
                    <tr 
                      key={entry.id} 
                      className={`border-t border-gray-700 hover:bg-gray-700/50 transition-all ${index % 2 === 0 ? 'bg-gray-800/50' : ''}`}
                    >
                      <td className="px-6 py-4 text-gray-300 text-sm">
                        {new Date(entry.timestamp).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          entry.entry_type === 'invoice' 
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                            : entry.entry_type === 'field_ticket'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                        }`}>
                          {entry.entry_type === 'invoice' ? 'Invoice' : entry.entry_type === 'field_ticket' ? 'Field Ticket (BOL)' : 'Swag Only'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-100 font-mono text-sm">
                        {entry.entry_number}
                      </td>
                      <td className="px-6 py-4 text-gray-100">
                        {entry.first_name} {entry.last_name}
                      </td>
                      <td className="px-6 py-4 text-gray-300 text-sm">
                        {entry.email}
                      </td>
                      <td className="px-6 py-4 text-gray-300 text-sm">
                        {entry.phone}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
          <div className="bg-gray-800 border border-green-500/30 p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Total Entries</p>
                <p className="text-3xl font-bold text-green-400">{entries.length}</p>
              </div>
              <Award className="text-green-400" size={48} />
            </div>
          </div>

          <div className="bg-gray-800 border border-green-500/30 p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Invoice Entries</p>
                <p className="text-3xl font-bold text-green-400">
                  {entries.filter(e => e.entry_type === 'invoice').length}
                </p>
              </div>
              <Calendar className="text-green-400" size={48} />
            </div>
          </div>

          <div className="bg-gray-800 border border-green-500/30 p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Field Ticket (BOL) Entries</p>
                <p className="text-3xl font-bold text-yellow-400">
                  {entries.filter(e => e.entry_type === 'field_ticket').length}
                </p>
              </div>
              <Calendar className="text-yellow-400" size={48} />
            </div>
          </div>

          <div className="bg-gray-800 border border-purple-500/30 p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Swag Only Entries</p>
                <p className="text-3xl font-bold text-purple-400">
                  {entries.filter(e => e.entry_type === 'swag_only').length}
                </p>
              </div>
              <Award className="text-purple-400" size={48} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrizeDrawAdmin;
